/**
 * ConversationsPanelContent
 *
 * Two-bucket messenger panel: Staff | Parents.
 * Each bucket mixes DMs and groups. Groups are visually distinct (stacked avatars).
 * Bucket assignment: if ANY non-staff participant → Parents bucket, else Staff.
 * Staff directory members without a conversation still appear (tap to start chat).
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSchool } from '@/contexts/SchoolContext';
import { useAuth } from '@/features/auth/hooks';
import UserService from '@/services/UserService';
import useConversations from '../hooks/useConversations';
import useMessengerStore from '@/stores/messengerStore';
import { createParticipant } from '@/models/Conversation';
import { Avatar, LoadingSpinner } from '@/components/ui';
import ConversationThread from './ConversationThread';
import { MagnifyingGlassIcon, UserGroupIcon, UserIcon } from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';

// ─── Filter pills ────────────────────────────────────────────────────────────

const FILTERS = [
  { key: 'staff', label: 'Staff' },
  { key: 'parents', label: 'Parents' },
];

function FilterPills({ active, onChange, unreadCounts }) {
  return (
    <div className="flex gap-1.5 px-3 py-2">
      {FILTERS.map(f => {
        const isActive = active === f.key;
        const unread = unreadCounts[f.key] || 0;
        return (
          <button
            key={f.key}
            onClick={() => onChange(f.key)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors flex items-center gap-1.5 ${
              isActive
                ? 'bg-primary text-primary-content'
                : 'bg-base-200 text-base-content/60 hover:bg-base-300'
            }`}
          >
            {f.label}
            {unread > 0 && (
              <span className={`min-w-[16px] h-4 rounded-full text-[10px] font-bold flex items-center justify-center px-1 ${
                isActive ? 'bg-primary-content/20 text-primary-content' : 'bg-error text-error-content'
              }`}>
                {unread > 99 ? '99+' : unread}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export default function ConversationsPanelContent() {
  const { schoolId } = useSchool();
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('staff');

  // Load school staff directory
  useEffect(() => {
    if (!schoolId) {
      setStaff([]);
      setStaffLoading(false);
      return;
    }
    setStaffLoading(true);
    UserService.getSchoolUsers(schoolId)
      .then(users => {
        setStaff(users.filter(u => u.id !== user?.uid));
        setStaffLoading(false);
      })
      .catch(err => {
        console.error('Error loading school staff:', err);
        setStaff([]);
        setStaffLoading(false);
      });
  }, [schoolId, user?.uid]);

  const {
    selectedConversation,
    mobileView,
    selectConversation,
    clearSelection
  } = useMessengerStore();

  const {
    conversations,
    loading: convoLoading
  } = useConversations({ limit: 50, schoolId });

  // Staff ID set for bucket classification
  const staffIds = useMemo(() => new Set(staff.map(s => s.id)), [staff]);

  // Classify each conversation: is it "parent-facing" or "staff-only"?
  const isParentConversation = useCallback((conv) => {
    const otherIds = (conv.participantIds || []).filter(id => id !== user?.uid);
    return otherIds.some(id => !staffIds.has(id));
  }, [staffIds, user?.uid]);

  // Build staff DM map (staffId → conversation)
  const staffConversationMap = useMemo(() => {
    const map = {};
    conversations.forEach(conv => {
      if (conv.type !== 'direct') return;
      if (isParentConversation(conv)) return; // skip parent DMs
      const other = conv.participantIds?.find(id => id !== user?.uid);
      if (other && !map[other]) map[other] = conv;
    });
    return map;
  }, [conversations, user?.uid, isParentConversation]);

  // ── STAFF bucket items ─────────────────────────────────────────────────────

  const staffBucketItems = useMemo(() => {
    // Staff DMs: merge directory + conversation data
    const dmItems = staff.map(member => {
      const conv = staffConversationMap[member.id];
      const unread = conv?.unreadCounts?.[user?.uid] || 0;
      // Only count lastMessageAt if there's an actual message — empty conversations sort to bottom
      const hasMessage = !!conv?.lastMessage;
      const lastMessageAt = hasMessage ? (conv?.lastMessageAt?.toDate?.() || null) : null;
      return {
        _kind: 'dm',
        id: member.id,
        displayName: member.displayName || member.email || 'Staff',
        email: member.email,
        photoURL: member.photoURL,
        roles: member.roles,
        conversation: conv || null,
        unreadCount: unread,
        lastMessageAt,
        _member: member, // keep for draft creation
      };
    });

    // Staff groups: groups where all participants are staff
    const groupItems = conversations
      .filter(c => c.type !== 'direct' && !isParentConversation(c))
      .map(conv => {
        const unread = conv.unreadCounts?.[user?.uid] || 0;
        const hasMessage = !!conv.lastMessage;
        const lastMessageAt = hasMessage ? (conv.lastMessageAt?.toDate?.() || null) : null;
        return {
          _kind: 'group',
          id: `group-${conv.id}`,
          displayName: conv.subject || conv.groupName || 'Group',
          conversation: conv,
          unreadCount: unread,
          lastMessageAt,
          participants: conv.participants,
        };
      });

    let combined = [...dmItems, ...groupItems];

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      combined = combined.filter(item =>
        item.displayName?.toLowerCase().includes(q) ||
        item.email?.toLowerCase().includes(q)
      );
    }

    // Sort: unread first → recent message → alphabetical
    combined.sort(sortByUnreadThenRecent);
    return combined;
  }, [staff, staffConversationMap, conversations, isParentConversation, user?.uid, searchQuery]);

  // ── PARENTS bucket items ───────────────────────────────────────────────────

  const parentBucketItems = useMemo(() => {
    const items = conversations
      .filter(conv => isParentConversation(conv))
      .map(conv => {
        const unread = conv.unreadCounts?.[user?.uid] || 0;
        const hasMessage = !!conv.lastMessage;
        const lastMessageAt = hasMessage ? (conv.lastMessageAt?.toDate?.() || null) : null;

        if (conv.type === 'direct') {
          const other = conv.participants?.find(p => p.userId !== user?.uid) || {};
          return {
            _kind: 'dm',
            id: other.userId || conv.participantIds?.find(id => id !== user?.uid),
            displayName: other.name || conv.subject || 'Parent',
            photoURL: other.photoURL || null,
            role: other.role || 'parent',
            conversation: conv,
            unreadCount: unread,
            lastMessageAt,
          };
        }

        // Group with parent participants
        return {
          _kind: 'group',
          id: `group-${conv.id}`,
          displayName: conv.subject || conv.groupName || 'Group',
          conversation: conv,
          unreadCount: unread,
          lastMessageAt,
          participants: conv.participants,
        };
      });

    let filtered = items;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = items.filter(item => item.displayName?.toLowerCase().includes(q));
    }

    filtered.sort(sortByUnreadThenRecent);
    return filtered;
  }, [conversations, isParentConversation, user?.uid, searchQuery]);

  // ── Unread counts per bucket (for pill badges) ─────────────────────────────

  const unreadCounts = useMemo(() => ({
    staff: staffBucketItems.reduce((sum, i) => sum + i.unreadCount, 0),
    parents: parentBucketItems.reduce((sum, i) => sum + i.unreadCount, 0),
  }), [staffBucketItems, parentBucketItems]);

  // ── Active bucket ──────────────────────────────────────────────────────────

  const activeItems = activeFilter === 'parents' ? parentBucketItems : staffBucketItems;

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleItemClick = useCallback((item) => {
    if (item.conversation) {
      selectConversation(item.conversation);
      return;
    }
    // No existing conversation — create a draft (staff DM only)
    if (item._member) {
      const memberName = item._member.displayName || item._member.email || 'Staff';
      const myName = user.displayName || user.email || 'Me';
      selectConversation({
        id: null,
        _draft: true,
        type: 'direct',
        subject: memberName,
        participants: [
          { ...createParticipant(user.uid, myName, 'teacher'), photoURL: user.photoURL || null },
          { ...createParticipant(item._member.id, memberName, 'teacher'), photoURL: item._member.photoURL || null }
        ],
        participantIds: [user.uid, item._member.id],
        schoolId,
        unreadCounts: {},
        lastMessage: null,
        lastMessageAt: null
      });
    }
  }, [user, schoolId, selectConversation]);

  const handleBack = () => clearSelection();
  const loading = staffLoading || convoLoading;

  // ── Thread view ────────────────────────────────────────────────────────────

  if (selectedConversation && mobileView === 'thread') {
    return (
      <div className="flex-1 flex flex-col overflow-hidden">
        <ConversationThread
          conversation={selectedConversation}
          conversations={conversations}
          onBack={handleBack}
          onToggleInfo={null}
          panelMode
        />
      </div>
    );
  }

  // ── List view ──────────────────────────────────────────────────────────────

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-base-100">
      {/* Search */}
      <div className="p-3 pb-0 border-b border-base-200">
        <div className="relative">
          <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-base-content/40" />
          <input
            type="search"
            placeholder={activeFilter === 'parents' ? 'Search parents...' : 'Search staff...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input input-bordered input-sm w-full pl-9"
          />
        </div>
        <FilterPills
          active={activeFilter}
          onChange={(key) => { setActiveFilter(key); setSearchQuery(''); }}
          unreadCounts={unreadCounts}
        />
      </div>

      {/* Items */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <LoadingSpinner />
          </div>
        ) : activeItems.length === 0 ? (
          <div className="p-6 text-center text-base-content/50">
            {searchQuery ? (
              <p className="text-sm">No results for "{searchQuery}"</p>
            ) : activeFilter === 'parents' ? (
              <p className="text-sm">No parent conversations yet</p>
            ) : (
              <p className="text-sm">No staff members found</p>
            )}
          </div>
        ) : (
          activeItems.map(item =>
            item._kind === 'group' ? (
              <GroupConversationItem
                key={item.id}
                conversation={item.conversation}
                currentUserId={user?.uid}
                onClick={() => selectConversation(item.conversation)}
                active={selectedConversation?.id === item.conversation?.id}
              />
            ) : (
              <ContactItem
                key={item.id}
                item={item}
                currentUserId={user?.uid}
                onClick={() => handleItemClick(item)}
              />
            )
          )
        )}
      </div>
    </div>
  );
}

// ─── Sorting helper ──────────────────────────────────────────────────────────

function sortByUnreadThenRecent(a, b) {
  if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
  if (b.unreadCount > 0 && a.unreadCount === 0) return 1;
  if (a.lastMessageAt && b.lastMessageAt) return b.lastMessageAt - a.lastMessageAt;
  if (a.lastMessageAt && !b.lastMessageAt) return -1;
  if (!a.lastMessageAt && b.lastMessageAt) return 1;
  return (a.displayName || '').localeCompare(b.displayName || '');
}

// ─── ContactItem — a single DM contact ──────────────────────────────────────

function ContactItem({ item, currentUserId, onClick }) {
  const hasConversation = !!item.conversation;
  const lastMessage = item.conversation?.lastMessage;
  const timeStr = item.lastMessageAt
    ? formatDistanceToNow(item.lastMessageAt, { addSuffix: false })
    : '';

  return (
    <button
      className="w-full px-4 py-3 hover:bg-base-200 transition-colors flex items-center gap-3 text-left"
      onClick={onClick}
    >
      <div className="flex-shrink-0 relative">
        <Avatar name={item.displayName || '?'} photoURL={item.photoURL} userId={item.id} size="sm" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className={`text-sm truncate ${item.unreadCount > 0 ? 'font-bold' : 'font-medium'}`}>
            {item.displayName}
          </span>
          {timeStr && (
            <span className="text-[10px] text-base-content/40 flex-shrink-0">{timeStr}</span>
          )}
        </div>

        {hasConversation && lastMessage ? (
          <p className={`text-xs truncate mt-0.5 ${item.unreadCount > 0 ? 'font-semibold text-base-content/80' : 'text-base-content/50'}`}>
            {lastMessage.senderId === item.id ? '' : 'You: '}
            {lastMessage.text}
          </p>
        ) : (
          <p className="text-xs text-base-content/30 mt-0.5 truncate">
            {item.roles?.length > 0 ? item.roles.join(', ') : 'Tap to message'}
          </p>
        )}
      </div>

      {item.unreadCount > 0 && (
        <span className="badge badge-primary badge-xs min-w-[18px] flex-shrink-0">
          {item.unreadCount}
        </span>
      )}
    </button>
  );
}

// ─── GroupConversationItem — a group conversation entry ───────────────────────

function GroupConversationItem({ conversation, currentUserId, onClick, active }) {
  const unread = conversation.unreadCounts?.[currentUserId] || 0;
  const lastMessage = conversation.lastMessage;
  const timeStr = conversation.lastMessageAt?.toDate
    ? formatDistanceToNow(conversation.lastMessageAt.toDate(), { addSuffix: false })
    : '';

  const groupName = conversation.subject || conversation.groupName || 'Group';
  const isSmartMessages = conversation.type === 'smart_messages';
  const isBroadcast =
    conversation.type === 'system' ||
    conversation.type === 'broadcast' ||
    isSmartMessages ||
    (conversation.type === 'group' && conversation.sourceType !== 'student');

  return (
    <button
      className={`w-full pr-4 py-3 hover:bg-base-200/80 transition-colors flex items-center gap-3 text-left ${
        isSmartMessages
          ? 'bg-base-100 border-l-4 border-primary pl-3'
          : `pl-4 ${isBroadcast ? 'bg-primary/10' : 'bg-base-200/40'}`
      } ${active ? 'bg-primary/5' : ''}`}
      onClick={onClick}
    >
      {/* Single avatar with group badge — same size as DM avatars */}
      <div className="flex-shrink-0 relative">
        <Avatar name={groupName} size="sm" className={isBroadcast ? '!rounded-lg' : ''} />
        <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-base-300 flex items-center justify-center ring-2 ring-base-100">
          <UserGroupIcon className="w-2.5 h-2.5 text-base-content/60" />
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className={`text-sm truncate ${unread > 0 ? 'font-bold' : 'font-medium'}`}>
            {groupName}
          </span>
          {timeStr && (
            <span className="text-[10px] text-base-content/40 flex-shrink-0">{timeStr}</span>
          )}
        </div>
        {lastMessage ? (
          <p className={`text-xs truncate mt-0.5 ${unread > 0 ? 'font-semibold text-base-content/80' : 'text-base-content/50'}`}>
            {lastMessage.senderName}: {lastMessage.text}
          </p>
        ) : (
          <p className="text-xs text-base-content/30 mt-0.5 truncate">Group conversation</p>
        )}
      </div>

      {unread > 0 && (
        <span className="badge badge-primary badge-xs min-w-[18px] flex-shrink-0">
          {unread}
        </span>
      )}
    </button>
  );
}
